import { zodResolver } from '@hookform/resolvers/zod'
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router'
import { z } from 'zod'

import { AuthLayout, Button, ErrorMessage, Field, Heading, Input, Label, Text } from '@ankor-io/ui-components'

import { iamService } from '@/api/services'
import { useConfig } from '@/hooks/useConfig'

const formSchema = z.object({
  email: z.string().email({
    message: 'Invalid email address.',
  }),
})

export function SignInPage() {
  const { config } = useConfig()
  const { login, isAuthenticated } = useKindeAuth()
  const navigate = useNavigate()

  const [doesAccountExist, setDoesAccountExist] = useState(true)
  const [lastSubmittedEmail, setLastSubmittedEmail] = useState<string | null>(null)

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
    },
  })

  // If already authenticated, redirect to dashboard
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/')
    }
  }, [isAuthenticated, navigate])

  // Reset the doesAccountExist error when email field is updated
  useEffect(() => {
    const currentEmail = form.watch('email')
    if (lastSubmittedEmail && currentEmail !== lastSubmittedEmail && !doesAccountExist) {
      setDoesAccountExist(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.watch('email'), lastSubmittedEmail])

  const handleDoesAccountExist = async (email: string) => {
    if (!email?.trim()) {
      setDoesAccountExist(false)
      return false
    }

    try {
      const exists = await iamService.checkAccountExists(email)
      setDoesAccountExist(exists)
      return exists
    } catch (error) {
      console.error('Failed to check account existence:', error)
      setDoesAccountExist(false)
      return false
    }
  }

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      setLastSubmittedEmail(values.email)
      const accountExists = await handleDoesAccountExist(values.email)
      if (!accountExists) {
        console.error('Account does not exist')
        return
      }

      // Make request to Kinde to signin
      const loginOptions = {
        orgCode: config?.kinde.org,
        connectionId: config?.kinde.connectionId,
        loginHint: values.email,
      }

      login(loginOptions)
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <AuthLayout>
      <div className="w-full max-w-md space-y-6">
        <div className="space-y-2 text-center">
          <Heading level={1}>Welcome to Lighthouse</Heading>
          <Text>Enter your credentials to sign in to your account</Text>
        </div>

        <form
          role="form"
          onSubmit={form.handleSubmit(handleSubmit)}
          className="space-y-4"
        >
          <Field>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="john@example.com"
              {...form.register('email')}
            />
            {form.formState.errors.email && <ErrorMessage>{form.formState.errors.email.message}</ErrorMessage>}
          </Field>

          <Button
            type="submit"
            className="w-full"
            disabled={form.formState.isSubmitting || !form.formState.isValid}
            color="primary"
          >
            {form.formState.isSubmitting ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>

        <div className="text-center">
          {!doesAccountExist && (
            <Field>
              <ErrorMessage className="text-red-500 mb-2">No account found with this email address.</ErrorMessage>
            </Field>
          )}
        </div>
      </div>
    </AuthLayout>
  )
}
